import { useEffect, useState } from 'react';
import { adminLearningPathsApi } from '@/services/adminLearningPaths';
import { Loader } from '@/components/custom/loader';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent, AccordionTitle } from '@/components/ui/accordion';
import { PerformanceBar } from '@/pages/learning-path/trilha-performance-utils';

// Descritor sem vínculo com conteúdo nunca gera aula: sinaliza em vez de mostrar 0%.
function DescriptorRow({ descriptor }) {
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-neutral-lightest bg-white p-3 sm:flex-row sm:items-center">
      <div className="flex items-center gap-2">
        <span className="w-20 shrink-0 truncate font-bold text-primary-dark">{descriptor.code}</span>
        <PerformanceBar
          percentage={descriptor.percentage}
          level={descriptor.level}
          compact
          className="ml-auto sm:hidden"
        />
      </div>
      <span
        className="min-w-0 flex-1 truncate text-neutral-dark"
        title={descriptor.description || descriptor.title}
      >
        {descriptor.title}
        {descriptor.covered === false && <span className="ml-2 text-xs text-neutral">(sem conteúdo vinculado)</span>}
      </span>
      <PerformanceBar
        percentage={descriptor.percentage}
        level={descriptor.level}
        compact
        className="hidden sm:flex"
      />
    </li>
  );
}

// Desempenho do aluno na trilha inteira: áreas → matérias → descritores, já ordenados
// pela API (pior → melhor, sem dados por último). `student.id` é o id do UserLearningPath da linha.
export default function TrilhaPerformanceModal({ open, onOpenChange, student }) {
  const [performance, setPerformance] = useState(null); // null = carregando
  const [error, setError] = useState(false);
  const userLearningPathId = student?.id;

  useEffect(() => {
    if (!open || !userLearningPathId) return;
    let active = true;
    setPerformance(null);
    setError(false);
    adminLearningPathsApi
      .studentPerformance(userLearningPathId)
      .then(json => {
        if (!active) return;
        const attrs = json?.data?.attributes;
        if (attrs) setPerformance(attrs);
        else setError(true);
      })
      .catch(() => active && setError(true));
    return () => {
      active = false;
    };
  }, [open, userLearningPathId]);

  const areas = performance?.areas || [];

  const renderContent = () => {
    if (error) return <p className="py-6 text-center text-sm text-neutral-dark">Não foi possível carregar o desempenho do aluno.</p>;
    if (!performance) return <Loader />;
    if (areas.length === 0) return <p className="py-6 text-center text-sm text-neutral-dark">O aluno ainda não tem desempenho nesta trilha.</p>;

    return (
      <Accordion
        type="multiple"
        className="min-w-0"
      >
        {areas.map(area => (
          <AccordionItem
            key={area.area}
            value={area.area}
            className="min-w-0"
          >
            <AccordionTrigger className="items-start px-5 py-4">
              <div className="min-w-0 flex-1">
                <AccordionTitle className="mb-2 block text-primary-dark">{area.area}</AccordionTitle>
                <PerformanceBar
                  percentage={area.percentage}
                  level={area.level}
                />
              </div>
            </AccordionTrigger>
            <AccordionContent>
              <div className="flex flex-col gap-5">
                {(area.subjects || []).map(subject => (
                  <section key={subject.subject}>
                    <div className="mb-2 flex items-center gap-3">
                      <span className="min-w-0 flex-1 truncate font-bold text-primary-dark">{subject.subject}</span>
                      <PerformanceBar
                        percentage={subject.percentage}
                        level={subject.level}
                        compact
                      />
                    </div>
                    {subject.descriptors?.length > 0 && (
                      <>
                        <div className="mb-2 hidden items-center gap-2 px-3 text-xs font-semibold text-neutral sm:flex">
                          <span className="w-20 shrink-0">Código</span>
                          <span className="min-w-0 flex-1 truncate">Descritor</span>
                          <span className="w-24 shrink-0 text-right">Desempenho</span>
                        </div>
                        <ul className="space-y-2">
                          {subject.descriptors.map(descriptor => (
                            <DescriptorRow
                              key={descriptor.id}
                              descriptor={descriptor}
                            />
                          ))}
                        </ul>
                      </>
                    )}
                  </section>
                ))}
              </div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    );
  };

  const studentName = student?.user_name || performance?.user_name;

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
    >
      <DialogContent
        size="lg"
        className="overflow-x-hidden"
      >
        <DialogHeader>
          <DialogTitle>Desempenho na Trilha</DialogTitle>
          {studentName && <DialogDescription>{studentName}</DialogDescription>}
        </DialogHeader>

        {renderContent()}
      </DialogContent>
    </Dialog>
  );
}
